import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import AdminAuthGate from '@/components/admin/AdminAuthGate';
import { parseChangelogProductFromPath } from '@/components/painel/changelog/changelog-painel-utils';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import { PainelShell } from '@/components/painel/PainelShell';
import { PainelDashboardHome } from '@/components/painel/PainelDashboardHome';
import { PainelSectionPlaceholder } from '@/components/painel/PainelSectionPlaceholder';
import { PainelCategoriesPage } from '@/components/painel/PainelCategoriesPage';
import { PainelArticlesPage } from '@/components/painel/PainelArticlesPage';
import { PainelArticleEditorPage } from '@/components/painel/PainelArticleEditorPage';
import { PainelPaginasHub } from '@/components/painel/PainelPaginasHub';
import { PainelHomepageEditorPage } from '@/components/painel/PainelHomepageEditorPage';
import { PainelRulesEditorPage } from '@/components/painel/PainelRulesEditorPage';
import { PainelStaffEditorPage } from '@/components/painel/PainelStaffEditorPage';
import { PainelEquipeTextsEditorPage } from '@/components/painel/PainelEquipeTextsEditorPage';
import { PainelAnalyticsOverviewPage } from '@/components/painel/analytics/PainelAnalyticsOverviewPage';
import { PainelAnalyticsTrafficPage } from '@/components/painel/analytics/PainelAnalyticsTrafficPage';
import { PainelAnalyticsPesquisasPage } from '@/components/painel/analytics/PainelAnalyticsPesquisasPage';
import { PainelAnalyticsGuiasPage } from '@/components/painel/analytics/PainelAnalyticsGuiasPage';
import { PainelAnalyticsPowersPage } from '@/components/painel/analytics/PainelAnalyticsPowersPage';
import { PainelAnalyticsCmsPage } from '@/components/painel/analytics/PainelAnalyticsCmsPage';
import { PainelAnalyticsUsersPage } from '@/components/painel/analytics/PainelAnalyticsUsersPage';
import { PainelChangelogOverviewPage } from '@/components/painel/changelog/PainelChangelogOverviewPage';
import { PainelChangelogVersionDetailPage } from '@/components/painel/changelog/PainelChangelogVersionDetailPage';
import { PainelChangelogVersionsPage } from '@/components/painel/changelog/PainelChangelogVersionsPage';
import { PainelChangelogCategoriesPage } from '@/components/painel/changelog/PainelChangelogCategoriesPage';
import { PainelChangelogEditorPage } from '@/components/painel/changelog/PainelChangelogEditorPage';
import { PainelPowersPage } from '@/components/painel/powers/PainelPowersPage';
import { PainelPowerEditorPage } from '@/components/painel/powers/PainelPowerEditorPage';
import { PainelUsuariosPage } from '@/components/painel/PainelUsuariosPage';
import { PainelPermissoesPage } from '@/components/painel/PainelPermissoesPage';
import { PainelAcesPage } from '@/components/painel/aces/PainelAcesPage';
import { PainelAceEditorPage } from '@/components/painel/aces/PainelAceEditorPage';
import { PainelTextosPage } from '@/components/painel/PainelTextosPage';
import { PainelConfiguracoesPage } from '@/components/painel/PainelConfiguracoesPage';
import { PainelComingSoonPage } from '@/components/painel/PainelComingSoonPage';
import { PainelFooterEditorPage } from '@/components/painel/PainelFooterEditorPage';
import { PainelSeoPage } from '@/components/painel/seo/PainelSeoPage';
import { PainelIconLibraryPage } from '@/components/painel/icon-library/PainelIconLibraryPage';
import { PainelRevisaoPage } from '@/components/painel/PainelRevisaoPage';
import { PainelSessionProvider } from '@/components/painel/rbac/painel-session-context';
import { PainelTextsProvider } from '@/components/painel/painel-texts-context';

function ChangelogRoutes() {
  return (
    <Routes>
      <Route index element={<Navigate to="xat/overview" replace />} />
      <Route path=":product/overview" element={<ChangelogOverviewRoute />} />
      <Route path=":product/versions" element={<ChangelogVersionsRoute />} />
      <Route path=":product/versions/novo" element={<ChangelogNewRoute />} />
      <Route path=":product/versions/:version" element={<ChangelogVersionDetailRoute />} />
      <Route path=":product/versions/:version/edit" element={<ChangelogEditRoute />} />
      <Route path=":product/categories" element={<ChangelogCategoriesRoute />} />
    </Routes>
  );
}

function useChangelogProduct(): ChangelogProductId {
  const { product } = useParams<{ product: string }>();
  if (product === 'ajuda' || product === 'xat') return product;
  return parseChangelogProductFromPath(window.location.pathname) ?? 'xat';
}

function ChangelogOverviewRoute() {
  const product = useChangelogProduct();
  return <PainelChangelogOverviewPage product={product} />;
}

function ChangelogVersionsRoute() {
  const product = useChangelogProduct();
  return <PainelChangelogVersionsPage product={product} />;
}

function ChangelogVersionDetailRoute() {
  const product = useChangelogProduct();
  const { version = '' } = useParams<{ version: string }>();
  return (
    <PainelChangelogVersionDetailPage
      product={product}
      version={decodeURIComponent(version)}
    />
  );
}

function ChangelogNewRoute() {
  const product = useChangelogProduct();
  return <PainelChangelogEditorPage key={`changelog-new-${product}`} product={product} mode="new" />;
}

function ChangelogEditRoute() {
  const product = useChangelogProduct();
  const { version = '' } = useParams<{ version: string }>();
  return (
    <PainelChangelogEditorPage
      key={`changelog-edit-${product}-${version}`}
      product={product}
      mode="edit"
      versionId={decodeURIComponent(version)}
    />
  );
}

function ChangelogCategoriesRoute() {
  const product = useChangelogProduct();
  return <PainelChangelogCategoriesPage product={product} />;
}

export default function PainelRouter() {
  return (
    <AdminAuthGate>
      <PainelSessionProvider>
        <PainelTextsProvider>
          <PainelShell>
            <Routes>
              <Route index element={<PainelDashboardHome />} />
              <Route path="categorias" element={<PainelCategoriesPage />} />
              <Route path="equipe" element={<PainelStaffEditorPage />} />
              <Route path="guias" element={<PainelArticlesPage />} />
              <Route path="guias/new" element={<PainelArticleEditorPage mode="new" />} />
              <Route path="guias/:id/edit" element={<PainelArticleEditorRoute mode="edit" />} />
              <Route path="revisao" element={<PainelRevisaoPage />} />
              <Route path="powers" element={<PainelPowersPage />} />
              <Route path="powers/new" element={<PainelPowerEditorPage mode="new" />} />
              <Route path="powers/:slug/edit" element={<PainelPowerEditorRoute mode="edit" />} />
              <Route path="aces" element={<PainelAcesPage />} />
              <Route path="aces/new" element={<PainelAceEditorPage mode="new" />} />
              <Route path="aces/:slug/edit" element={<PainelAceEditorRoute mode="edit" />} />
              <Route path="analytics" element={<PainelAnalyticsOverviewPage />} />
              <Route path="analytics/trafego" element={<PainelAnalyticsTrafficPage />} />
              <Route path="analytics/pesquisas" element={<PainelAnalyticsPesquisasPage />} />
              <Route path="analytics/guias" element={<PainelAnalyticsGuiasPage />} />
              <Route path="analytics/powers" element={<PainelAnalyticsPowersPage />} />
              <Route path="analytics/cms" element={<PainelAnalyticsCmsPage />} />
              <Route path="analytics/usuarios" element={<PainelAnalyticsUsersPage />} />
              <Route path="changelog/*" element={<ChangelogRoutes />} />
              <Route path="paginas" element={<PainelPaginasHub />} />
              <Route path="paginas/homepage" element={<PainelHomepageEditorPage />} />
              <Route path="paginas/regras" element={<PainelRulesEditorPage />} />
              <Route path="paginas/conduta" element={<PainelRulesEditorPage />} />
              <Route path="paginas/equipe" element={<PainelEquipeTextsEditorPage />} />
              <Route path="usuarios" element={<PainelUsuariosPage />} />
              <Route path="permissoes" element={<PainelPermissoesPage />} />
              <Route path="textos" element={<PainelTextosPage />} />
              <Route path="footer" element={<PainelFooterEditorPage />} />
              <Route path="identidade" element={<PainelFooterEditorPage />} />
              <Route path="coming-soon" element={<PainelComingSoonPage />} />
              <Route path="seo" element={<PainelSeoPage />} />
              <Route path="icon-library" element={<PainelIconLibraryPage />} />
              <Route path="configuracoes" element={<PainelConfiguracoesPage />} />
              <Route path=":slug" element={<PainelPlaceholderRoute />} />
            </Routes>
          </PainelShell>
        </PainelTextsProvider>
      </PainelSessionProvider>
    </AdminAuthGate>
  );
}

function PainelPlaceholderRoute() {
  const slug = window.location.pathname.split('/').pop() ?? '';
  return <PainelSectionPlaceholder slug={slug} />;
}

function PainelArticleEditorRoute({ mode }: { mode: 'edit' }) {
  const { id = '' } = useParams<{ id: string }>();
  return <PainelArticleEditorPage mode={mode} articleId={id} />;
}

function PainelPowerEditorRoute({ mode }: { mode: 'edit' }) {
  const { slug = '' } = useParams<{ slug: string }>();
  return <PainelPowerEditorPage mode={mode} powerSlug={decodeURIComponent(slug)} />;
}

function PainelAceEditorRoute({ mode }: { mode: 'edit' }) {
  const { slug = '' } = useParams<{ slug: string }>();
  return <PainelAceEditorPage mode={mode} aceSlug={decodeURIComponent(slug)} />;
}
